import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save, RotateCcw, Loader2 } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";

const DEF = { scale: 100, dx: 0, dy: 0 };
const SLIDERS = [["scale", "Größe", 50, 150, 5, "%"], ["dx", "Links / rechts", -30, 30, 1, "mm"], ["dy", "Runter / hoch", -20, 20, 1, "mm"]];

function Group({ kind, title, v, onChange }) {
  return (
    <div className="space-y-2" data-testid={`traeger-adjust-${kind}`}>
      <div className="text-sm font-medium text-slate-800">{title}</div>
      {SLIDERS.map(([k, l, min, max, step, unit]) => (
        <label key={k} className="grid grid-cols-[110px_1fr_56px] items-center gap-2 text-xs text-slate-600">
          {l}
          <input type="range" min={min} max={max} step={step} value={v[k]} onChange={(e) => onChange({ ...v, [k]: Number(e.target.value) })} data-testid={`traeger-adjust-${kind}-${k}`} />
          <span className="font-mono text-right">{v[k] > 0 && k !== "scale" ? "+" : ""}{v[k]} {unit}</span>
        </label>
      ))}
    </div>
  );
}

export function TraegerImageAdjust({ initial, hasImages }) {
  const [adj, setAdj] = useState({ stamp: { ...DEF, ...(initial?.stamp || {}) }, signature: { ...DEF, ...(initial?.signature || {}) } });
  const [url, setUrl] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!hasImages) return undefined;
    let u;
    const t = setTimeout(() => {
      const params = Object.fromEntries(["stamp", "signature"].flatMap((k) => Object.entries(adj[k]).map(([p, v]) => [`${k}_${p}`, v])));
      api.get("/traeger-forms/profile/image-preview", { params, responseType: "blob" })
        .then((r) => { u = URL.createObjectURL(r.data); setUrl((old) => { if (old) URL.revokeObjectURL(old); return u; }); }).catch(() => {});
    }, 300);
    return () => clearTimeout(t);
  }, [adj, hasImages]);
  if (!hasImages) return null;
  const save = async () => {
    setBusy(true);
    try { await api.put("/traeger-forms/profile/image-adjust", adj); toast.success("Position und Größe gespeichert – gilt für alle Trägerbestätigungen"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); } finally { setBusy(false); }
  };
  return (
    <div className="border border-slate-200 p-4 space-y-4" data-testid="traeger-image-adjust">
      <div className="text-sm font-medium text-slate-900">Größe und Position von Stempel und Unterschrift</div>
      <div className="grid md:grid-cols-2 gap-6">
        <Group kind="signature" title="Unterschrift" v={adj.signature} onChange={(v) => setAdj({ ...adj, signature: v })} />
        <Group kind="stamp" title="Stempel" v={adj.stamp} onChange={(v) => setAdj({ ...adj, stamp: v })} />
      </div>
      <div className="border border-slate-200 bg-white min-h-[120px] flex items-center justify-center" data-testid="traeger-adjust-preview">
        {url ? <img src={url} alt="Vorschau" className="max-w-full" /> : <Loader2 size={16} className="animate-spin text-slate-300" />}
      </div>
      <div className="flex gap-2">
        <button onClick={save} disabled={busy} className="inline-flex items-center gap-2 bg-slate-900 text-white px-4 py-2 text-sm hover:bg-slate-700 disabled:opacity-50" data-testid="traeger-adjust-save"><Save size={14} /> Speichern</button>
        <button onClick={() => setAdj({ stamp: { ...DEF }, signature: { ...DEF } })} className="inline-flex items-center gap-2 border border-slate-300 px-3 py-2 text-sm hover:bg-slate-50" data-testid="traeger-adjust-reset"><RotateCcw size={13} /> Zurücksetzen</button>
      </div>
    </div>
  );
}
